/**
 * SuggestedControlsPanel Component
 * Displays AI-suggested controls for policy documents with accept/reject workflow
 * Issue #312: Updated to show both Internal Controls (recommended) and Framework Controls
 */

import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import {
  Sparkles,
  Check,
  X,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Link2,
  Layers,
} from 'lucide-react'

type Suggestion = {
  id: string
  document_id: string
  control_id?: string
  internal_control_id?: string
  suggestion_type?: 'internal_control' | 'framework'
  is_internal_control?: boolean
  confidence_score: number
  match_reason: string
  status: 'pending' | 'accepted' | 'rejected'
  control_number: string
  control_name: string
  control_description: string
  framework_name?: string
  category?: string
  evidence_guidance?: string
  framework_count?: number
  reviewed_by_username?: string
  reviewed_at?: number
}

interface SuggestedControlsPanelProps {
  documentId: string
  documentCategory: string
  onLinksUpdated?: () => void
}

export default function SuggestedControlsPanel({
  documentId,
  documentCategory,
  onLinksUpdated,
}: SuggestedControlsPanelProps) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(true)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showHistory, setShowHistory] = useState(false)
  const [activeTab, setActiveTab] = useState<'internal' | 'framework'>('internal')

  // Only show for policy/procedure/standard documents
  const isEligible = ['policy', 'procedure', 'standard'].includes(documentCategory)

  useEffect(() => {
    if (isEligible) {
      loadSuggestions()
    }
  }, [documentId, isEligible])

  const loadSuggestions = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.getDocumentSuggestions(documentId)
      if (response.success) {
        setSuggestions(response.suggestions)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load suggestions')
    } finally {
      setLoading(false)
    }
  }

  const handleAnalyze = async () => {
    setAnalyzing(true)
    setError(null)
    try {
      const response = await api.analyzeDocumentControls(documentId)
      if (response.success) {
        await loadSuggestions()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed')
    } finally {
      setAnalyzing(false)
    }
  }

  const handleAccept = async (suggestionIds: string[]) => {
    try {
      await api.acceptDocumentSuggestions(documentId, { suggestion_ids: suggestionIds })
      await loadSuggestions()
      setSelectedIds(new Set())
      onLinksUpdated?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept suggestions')
    }
  }

  const handleReject = async (suggestionIds: string[]) => {
    try {
      await api.rejectDocumentSuggestions(documentId, { suggestion_ids: suggestionIds })
      await loadSuggestions()
      setSelectedIds(new Set())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject suggestions')
    }
  }

  const handleSelectAll = (forInternalControls: boolean) => {
    const pendingIds = suggestions
      .filter(s => s.status === 'pending' && (s.is_internal_control === forInternalControls))
      .map(s => s.id)
    const currentSelected = Array.from(selectedIds).filter(id =>
      suggestions.find(s => s.id === id)?.is_internal_control === forInternalControls
    )
    if (currentSelected.length === pendingIds.length) {
      // Deselect all for this type
      const newSelected = new Set(selectedIds)
      pendingIds.forEach(id => newSelected.delete(id))
      setSelectedIds(newSelected)
    } else {
      // Select all for this type
      setSelectedIds(new Set([...selectedIds, ...pendingIds]))
    }
  }

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  if (!isEligible) {
    return null
  }

  // Separate suggestions by type
  const internalControlSuggestions = suggestions.filter(s => s.is_internal_control === true)
  const frameworkSuggestions = suggestions.filter(s => s.is_internal_control !== true)

  const pendingIC = internalControlSuggestions.filter(s => s.status === 'pending')
  const pendingFC = frameworkSuggestions.filter(s => s.status === 'pending')
  const historyIC = internalControlSuggestions.filter(s => s.status !== 'pending')
  const historyFC = frameworkSuggestions.filter(s => s.status !== 'pending')

  const selectedArray = Array.from(selectedIds)
  const selectedICIds = selectedArray.filter(id =>
    internalControlSuggestions.some(s => s.id === id)
  )
  const selectedFCIds = selectedArray.filter(id =>
    frameworkSuggestions.some(s => s.id === id)
  )

  const getConfidenceColor = (score: number) => {
    if (score >= 0.85) return 'text-green-600 bg-green-50'
    if (score >= 0.7) return 'text-yellow-600 bg-yellow-50'
    return 'text-orange-600 bg-orange-50'
  }

  const renderSuggestionItem = (suggestion: Suggestion, isInternal: boolean) => (
    <div
      key={suggestion.id}
      className={`flex items-start gap-3 p-3 rounded-lg hover:bg-gray-100 ${
        isInternal ? 'bg-purple-50/50' : 'bg-gray-50'
      }`}
    >
      <input
        type="checkbox"
        checked={selectedIds.has(suggestion.id)}
        onChange={() => toggleSelection(suggestion.id)}
        className="mt-1 h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900">
            {suggestion.control_number}
          </span>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getConfidenceColor(suggestion.confidence_score)}`}>
            {Math.round(suggestion.confidence_score * 100)}% match
          </span>
          {isInternal ? (
            <>
              <span className="text-xs text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">
                {suggestion.category}
              </span>
              {suggestion.framework_count && suggestion.framework_count > 0 && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Link2 className="h-3 w-3" />
                  Satisfies {suggestion.framework_count} controls
                </span>
              )}
            </>
          ) : (
            <span className="text-xs text-gray-500">
              {suggestion.framework_name}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-700 mt-0.5">
          {suggestion.control_name}
        </p>
        <p className="text-xs text-gray-500 mt-1 italic">
          {suggestion.match_reason}
        </p>
        {isInternal && suggestion.evidence_guidance && (
          <p className="text-xs text-purple-600 mt-1">
            Evidence needed: {suggestion.evidence_guidance}
          </p>
        )}
      </div>
      <div className="flex gap-1">
        <button
          onClick={() => handleAccept([suggestion.id])}
          className="p-1 text-green-600 hover:bg-green-100 rounded"
          title="Accept"
        >
          <Check className="h-5 w-5" />
        </button>
        <button
          onClick={() => handleReject([suggestion.id])}
          className="p-1 text-red-600 hover:bg-red-100 rounded"
          title="Reject"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  )

  const renderBulkActions = (selectedCount: number, ids: string[]) => {
    if (selectedCount === 0) return null
    return (
      <div className="mb-3 flex items-center gap-2 p-2 bg-gray-100 rounded">
        <span className="text-sm text-gray-600">
          {selectedCount} selected
        </span>
        <button
          onClick={() => handleAccept(ids)}
          className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
        >
          <Check className="h-3 w-3 mr-1" />
          Accept
        </button>
        <button
          onClick={() => handleReject(ids)}
          className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700"
        >
          <X className="h-3 w-3 mr-1" />
          Reject
        </button>
      </div>
    )
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-500" />
          <h3 className="font-medium text-gray-900">AI Suggested Controls</h3>
          {(pendingIC.length > 0 || pendingFC.length > 0) && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              {pendingIC.length + pendingFC.length} pending
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleAnalyze()
            }}
            disabled={analyzing}
            className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${analyzing ? 'animate-spin' : ''}`} />
            {analyzing ? 'Analyzing...' : 'Re-analyze'}
          </button>
          {expanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-200">
          {error && (
            <div className="px-4 py-2 bg-red-50 text-red-600 text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          {loading ? (
            <div className="px-4 py-8 text-center text-gray-500">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
              Loading suggestions...
            </div>
          ) : suggestions.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">
              <Sparkles className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>No suggestions yet</p>
              <p className="text-sm mt-1">Click "Re-analyze" to find matching controls</p>
            </div>
          ) : (
            <>
              {/* Tabs for Internal Controls vs Framework Controls */}
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('internal')}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    activeTab === 'internal'
                      ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Layers className="h-4 w-4" />
                    <span>Internal Controls</span>
                    {pendingIC.length > 0 && (
                      <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                        {pendingIC.length}
                      </span>
                    )}
                    <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                      Recommended
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('framework')}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    activeTab === 'framework'
                      ? 'text-gray-700 border-b-2 border-gray-400 bg-gray-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span>Framework Controls</span>
                    {pendingFC.length > 0 && (
                      <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-700 rounded">
                        {pendingFC.length}
                      </span>
                    )}
                    <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">
                      Legacy
                    </span>
                  </div>
                </button>
              </div>

              {/* Internal Controls Tab */}
              {activeTab === 'internal' && (
                <div className="p-4">
                  {pendingIC.length === 0 && historyIC.length === 0 ? (
                    <div className="py-4 text-center text-gray-500">
                      <Layers className="h-6 w-6 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No internal control suggestions</p>
                      <p className="text-xs mt-1">Internal controls satisfy multiple framework requirements at once</p>
                    </div>
                  ) : (
                    <>
                      {pendingIC.length > 0 && (
                        <>
                          {renderBulkActions(selectedICIds.length, selectedICIds)}

                          <div className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              checked={selectedICIds.length === pendingIC.length && pendingIC.length > 0}
                              onChange={() => handleSelectAll(true)}
                              className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                            />
                            <span className="ml-2 text-sm text-gray-600">Select all</span>
                          </div>

                          <div className="space-y-2">
                            {pendingIC.map((s) => renderSuggestionItem(s, true))}
                          </div>
                        </>
                      )}

                      {/* History for internal controls */}
                      {historyIC.length > 0 && (
                        <div className={pendingIC.length > 0 ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
                          <button
                            onClick={() => setShowHistory(!showHistory)}
                            className="w-full text-sm text-gray-600 hover:text-gray-800 flex items-center gap-2"
                          >
                            <span>History ({historyIC.length})</span>
                            {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                          {showHistory && (
                            <div className="mt-2 space-y-1">
                              {historyIC.map((s) => (
                                <div key={s.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm">
                                  {s.status === 'accepted' ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-red-500" />
                                  )}
                                  <span className="font-medium text-gray-700">{s.control_number}</span>
                                  <span className="text-gray-500 truncate flex-1">{s.control_name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Framework Controls Tab */}
              {activeTab === 'framework' && (
                <div className="p-4">
                  {pendingFC.length === 0 && historyFC.length === 0 ? (
                    <div className="py-4 text-center text-gray-500">
                      <p className="text-sm">No framework control suggestions</p>
                    </div>
                  ) : (
                    <>
                      {pendingFC.length > 0 && (
                        <>
                          {renderBulkActions(selectedFCIds.length, selectedFCIds)}

                          <div className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              checked={selectedFCIds.length === pendingFC.length && pendingFC.length > 0}
                              onChange={() => handleSelectAll(false)}
                              className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                            />
                            <span className="ml-2 text-sm text-gray-600">Select all</span>
                          </div>

                          <div className="space-y-2">
                            {pendingFC.map((s) => renderSuggestionItem(s, false))}
                          </div>
                        </>
                      )}

                      {/* History for framework controls */}
                      {historyFC.length > 0 && (
                        <div className={pendingFC.length > 0 ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
                          <button
                            onClick={() => setShowHistory(!showHistory)}
                            className="w-full text-sm text-gray-600 hover:text-gray-800 flex items-center gap-2"
                          >
                            <span>History ({historyFC.length})</span>
                            {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                          {showHistory && (
                            <div className="mt-2 space-y-1">
                              {historyFC.map((s) => (
                                <div key={s.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm">
                                  {s.status === 'accepted' ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-red-500" />
                                  )}
                                  <span className="font-medium text-gray-700">{s.control_number}</span>
                                  <span className="text-gray-500 truncate flex-1">{s.control_name}</span>
                                  <span className="text-xs text-gray-400">{s.framework_name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
